import React, { useEffect, useMemo, useState } from "react";
import { fmtCurrency, fmtDate } from "../../utils/format";
import billingIcon from "../../assets/billing-icon.png";
import { ColumnPicker } from "../../components/table/ColumnPicker";
import { useColumnPreferences } from "../../components/table/useColumnPreferences";
import { useColumnResizing } from "../../components/table/useColumnResizing";
import { useBillingEntries } from "./hooks/useBillingEntries";

const DEFAULT_COLUMN_WIDTH = 220;
const MIN_COLUMN_WIDTH = 10;
const MAX_COLUMN_WIDTH = 480;

function pickValue(row, ...candidates) {
  for (const key of candidates) {
    if (!key) continue;
    if (Object.prototype.hasOwnProperty.call(row, key)) {
      const direct = row[key];
      if (direct !== undefined && direct !== null && direct !== "") return direct;
    }
    const parts = key.split(".");
    let ptr = row;
    let ok = true;
    for (const part of parts) {
      if (ptr && Object.prototype.hasOwnProperty.call(ptr, part)) {
        ptr = ptr[part];
      } else if (ptr && part in ptr) {
        ptr = ptr[part];
      } else {
        ok = false;
        break;
      }
    }
    if (ok && ptr !== undefined && ptr !== null && ptr !== "") return ptr;
  }
  return null;
}

function makeCell(value, options = {}) {
  const { title, align = "left", monospace = false } = options;
  let display;
  if (value === undefined || value === null || value === "") {
    display = "—";
  } else {
    display = value;
  }
  const displayText = typeof display === "string" ? display : String(display);
  return {
    display: displayText,
    title: title ?? (displayText === "—" ? undefined : displayText),
    align,
    monospace,
  };
}

const BILLING_COLUMN_SPECS = [
  {
    id: "row_number",
    label: "#",
    accessor: (_row, index) => makeCell(index + 1, { align: "center" }),
  },
  {
    id: "id",
    label: "ID",
    accessor: (row) => makeCell(pickValue(row, "id", "pkid"), { monospace: true }),
  },
  {
    id: "occurred_at",
    label: "OccurredAt",
    accessor: (row) => makeCell(fmtDate(pickValue(row, "occurredAt", "occurred_at")) || "—"),
  },
  {
    id: "type_id",
    label: "Type.id",
    accessor: (row) => {
      const value = pickValue(row, "typeId", "type.id", "type_id");
      const category = pickValue(row, "typeCategory", "type.category");
      return makeCell(value, { title: category ? `${value} (${category})` : undefined });
    },
  },
  {
    id: "type_name",
    label: "Type.name",
    accessor: (row) =>
      makeCell(pickValue(row, "typeName", "type.name", "type_name"), {
        title: pickValue(row, "type.description"),
      }),
  },
  {
    id: "order_id",
    label: "Order ID",
    accessor: (row) =>
      makeCell(pickValue(row, "orderId", "order.allegroId", "order.id", "order_reference", "order.reference", "order")),
  },
  {
    id: "is_marketing",
    label: "Marketing",
    accessor: (row) => makeCell(pickValue(row, "isMarketing") ? "Yes" : "No", { align: "center" }),
  },
  {
    id: "offer_id",
    label: "Offer ID",
    accessor: (row) => makeCell(pickValue(row, "offerId", "offer.id", "offer_id", "offer")),
  },
  {
    id: "offer_name",
    label: "Offer name",
    accessor: (row) => makeCell(pickValue(row, "offerName", "offer.name", "offer_name")),
  },
  {
    id: "value_amount",
    label: "Value amount",
    accessor: (row) =>
      makeCell(
        fmtCurrency(
          Number(pickValue(row, "valueAmount", "amount", "value.amount") ?? 0),
          "pl-PL",
          pickValue(row, "valueCurrency", "currency", "value.currency") || "PLN",
        ),
        { align: "right" },
      ),
  },
  {
    id: "value_currency",
    label: "Value currency",
    accessor: (row) =>
      makeCell(pickValue(row, "valueCurrency", "currency", "value.currency") || "PLN", { align: "center" }),
  },
  {
    id: "tax_percentage",
    label: "Tax %",
    accessor: (row) => {
      const raw = pickValue(row, "taxPercentage", "tax.percentage", "tax_percentage");
      return makeCell(raw != null ? `${Number(raw).toFixed(2)}%` : "—", { align: "right" });
    },
  },
  {
    id: "balance_amount",
    label: "Balance amount",
    accessor: (row) =>
      makeCell(
        fmtCurrency(
          Number(pickValue(row, "balanceAmount", "balance.amount") ?? 0),
          "pl-PL",
          pickValue(row, "balanceCurrency", "balance.currency") || "PLN",
        ),
        { align: "right" },
      ),
  },
  {
    id: "balance_currency",
    label: "Balance currency",
    accessor: (row) =>
      makeCell(pickValue(row, "balanceCurrency", "balance.currency") || "PLN", { align: "center" }),
  },
];

const DEFAULT_VISIBLE_COLUMN_IDS = BILLING_COLUMN_SPECS.map((spec) => spec.id);

export function BillingPanel() {
  const [dateFromLocal, setDateFromLocal] = useState("");
  const [dateToLocal, setDateToLocal] = useState("");
  const [marketplaceId, setMarketplaceId] = useState("allegro-pl");

  const allColumnIds = useMemo(() => BILLING_COLUMN_SPECS.map((spec) => spec.id), []);

  const {
    visibleColumns,
    toggleColumn: toggleColumnVisibility,
    resetColumns,
    selectAllColumns,
  } = useColumnPreferences({
    allColumnIds,
    defaultVisibleIds: DEFAULT_VISIBLE_COLUMN_IDS,
    storageKey: "billing.visibleColumns",
  });

  const { columnWidths, updateColumnWidth, startColumnResize } = useColumnResizing({
    minWidth: MIN_COLUMN_WIDTH,
    maxWidth: MAX_COLUMN_WIDTH,
  });

  const toYMD = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");
  const dateFrom = useMemo(() => toYMD(dateFromLocal), [dateFromLocal]);
  const dateTo = useMemo(() => toYMD(dateToLocal), [dateToLocal]);
  const { rows, count, balance, loading, error, reload } = useBillingEntries({
    dateFrom,
    dateTo,
    marketplaceId,
  });

  useEffect(() => {
    const onRefresh = () => reload();
    window.addEventListener("billing:refresh", onRefresh);
    return () => window.removeEventListener("billing:refresh", onRefresh);
  }, [reload]);

  const columnOptions = useMemo(
    () => BILLING_COLUMN_SPECS.map((spec) => ({ id: spec.id, label: spec.label })),
    [],
  );

  const visibleColumnDefs = useMemo(
    () => BILLING_COLUMN_SPECS.filter((spec) => visibleColumns.includes(spec.id)),
    [visibleColumns],
  );

  const tableRows = useMemo(
    () =>
      rows.map((record, index) => {
        const values = {};
        BILLING_COLUMN_SPECS.forEach((spec) => {
          values[spec.id] = spec.accessor(record, index);
        });
        return { record, index, values };
      }),
    [rows],
  );

  const handleToggleColumn = toggleColumnVisibility;
  const handleResetColumns = resetColumns;
  const handleSelectAllColumns = selectAllColumns;

  return (
    <div className="bg-white rounded-xl shadow p-6 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-3xl font-bold flex items-center">
          <img src={billingIcon} alt="Panel" className="mr-3 h-8 w-8 object-contain" /> Billing
        </h3>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1">From</label>
          <input
            type="datetime-local"
            value={dateFromLocal}
            onChange={(e) => setDateFromLocal(e.target.value)}
            className="border rounded px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">To</label>
          <input
            type="datetime-local"
            value={dateToLocal}
            onChange={(e) => setDateToLocal(e.target.value)}
            className="border rounded px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Marketplace</label>
          <select
            value={marketplaceId}
            onChange={(e) => setMarketplaceId(e.target.value)}
            className="border rounded px-3 py-2 text-sm"
          >
            <option value="allegro-pl">allegro-pl</option>
          </select>
        </div>
        <div className="ml-auto max-w-xs text-sm text-slate-500">
          <p className="leading-snug">
            Run the Allegro import from Account Settings to sync new billing entries, then tweak these filters to view the latest data.
          </p>
          {loading && <p className="mt-1 font-medium text-slate-600">Loading billing records…</p>}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-4">
        <h3 className="text-lg font-semibold">Operations</h3>
        <div className="flex items-center gap-6 text-sm text-gray-700">
          <div>
            <strong>Number of items:</strong> {count}
          </div>
          <div>
            <strong>Balance:</strong> {fmtCurrency(balance, "pl-PL", "PLN")}
          </div>
          <ColumnPicker
            options={columnOptions}
            visibleColumns={visibleColumns}
            onToggle={handleToggleColumn}
            onReset={handleResetColumns}
            onSelectAll={handleSelectAllColumns}
            triggerLabel="Columns"
            triggerClassName="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
            panelClassName="w-64"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full table-fixed text-left text-sm border border-gray-200">
          <thead>
            <tr className="bg-gray-50 text-gray-700 select-none">
              {visibleColumnDefs.map((column) => {
                const widthValue = columnWidths[column.id];
                const style =
                  widthValue !== undefined
                    ? {
                        width: `${widthValue}px`,
                        minWidth: `${widthValue}px`,
                        maxWidth: `${widthValue}px`,
                      }
                    : { minWidth: `${MIN_COLUMN_WIDTH}px` };
                return (
                  <th
                    key={column.id}
                    className="px-4 py-3 text-sm font-semibold border-b relative group overflow-hidden border-r border-gray-200 last:border-r-0"
                    style={style}
                  >
                    <div className="pr-2 w-full overflow-hidden">
                      <span className="block truncate" title={column.label}>
                        {column.label}
                      </span>
                    </div>
                    <div
                      className="absolute top-0 right-0 flex h-full w-2 cursor-col-resize items-center justify-center"
                      onMouseDown={(event) => startColumnResize(column.id, event)}
                      onDoubleClick={() => updateColumnWidth(column.id, null)}
                    >
                      <span className="block h-3/4 w-px bg-gray-300 group-hover:bg-gray-500 transition-colors" />
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {tableRows.length === 0 ? (
              <tr>
                <td
                  colSpan={Math.max(1, visibleColumnDefs.length)}
                  className="px-4 py-6 text-center text-gray-600"
                >
                  No billing data yet. Run the Allegro import from Account Settings and adjust the filters to view synced entries.
                </td>
              </tr>
            ) : (
              tableRows.map((row) => (
                <tr
                  key={(row.record && (row.record.id || row.record.pkid)) || row.index}
                  className={`${row.index % 2 === 1 ? "bg-gray-50" : "bg-white"} hover:bg-gray-100`}
                >
                  {visibleColumnDefs.map((column) => {
                    const cell = row.values[column.id] || makeCell("—");
                    const alignClass =
                      cell.align === "right"
                        ? "text-right"
                        : cell.align === "center"
                        ? "text-center"
                        : "text-left";
                    const fontClass = cell.monospace ? "font-mono" : "";
                    const widthValue = columnWidths[column.id];
                    const style =
                      widthValue !== undefined
                        ? {
                            width: `${widthValue}px`,
                            minWidth: `${widthValue}px`,
                            maxWidth: `${widthValue}px`,
                          }
                        : { minWidth: `${MIN_COLUMN_WIDTH}px` };
                    return (
                      <td
                        key={column.id}
                        className={`px-4 py-3 ${alignClass} ${fontClass} border-r border-gray-200 last:border-r-0 whitespace-nowrap ${widthValue !== undefined ? "truncate" : ""}`}
                        style={style}
                        title={cell.title}
                      >
                        {cell.display}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
